import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Logout from "./Logout";
import { getTokenInternal } from "./useToken";
import FriendIcon from "./FriendIcon";
import AnnouncementBar from "./AnnouncementBar";
import { Menu, X, ChevronDown, ChevronUp } from "lucide-react"; // Optional icons, or use another

function Nav() {
  const location = useLocation();
  const [token, setToken] = useState();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [gamesNameOptions, setGamesNameOptions] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reviewCounts, setReviewCounts] = useState({ suggestions: 0, translations: 0 });
  const [claimsCount, setClaimsCount] = useState(0);
  const myItemsDropdownRef = useRef(null);
  const manageDropdownRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const puzzlesDropdownRef = useRef(null);
  const navRef = useRef(null);


  const toggleDropdown = async (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);

    if (dropdownName === "MyItemTrackers" && openDropdown !== dropdownName) {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/games`);
        if (!res.ok) throw new Error("Failed to fetch games");
        const games = await res.json();
        setGamesNameOptions(games);
      } catch (err) {
        console.error("Error fetching dropdown options:", err);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setMenuOpen(false);
        return;
      }
      const dropdownRefs = [
        myItemsDropdownRef,
        manageDropdownRef,
        accountDropdownRef,
        puzzlesDropdownRef,
      ];
      const clickedInsideDropdown = dropdownRefs.some(
        (ref) => ref.current && ref.current.contains(event.target)
      );
      if (!clickedInsideDropdown) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    getTokenInternal()
      .then(async (t) => {
        setToken(t);
        if (t) {
          try {
            const claimsRes = await fetch(
              `${process.env.REACT_APP_API_HOST}/api/claims/pending`,
              { headers: { Authorization: `Bearer ${t.access_token}` } }
            );
            if (claimsRes.ok) {
              const claimsData = await claimsRes.json();
              setClaimsCount(claimsData.length);
            }
          } catch {}
        }
        if (t && ["owner", "management"].includes(t.account.role)) {
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_HOST}/api/review/counts`,
              { headers: { Authorization: `Bearer ${t.access_token}` } }
            );
            if (res.ok) setReviewCounts(await res.json());
          } catch {}
        }
      })
      .catch((err) => console.error("Error fetching token:", err));
  }, [location]);

  return (
    <>
    <nav
      ref={navRef}
      className="relative z-20 bg-black w-full h-20 px-4 flex items-center justify-between border-b-card border-t-darker border-8"
    >
      {/* Logo and Home */}
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <img
            src={require("../images/Unofficial.PNG")}
            alt="Logo"
            className="h-16 shadow-lg"
          />
          <span className="text-white font-medium ml-2 hover:text-gray-400 transition duration-300">
            Home
          </span>
        </Link>
      </div>

      {/* Mobile Menu Toggle */}
      <div className="xl:hidden">
        <button onClick={() => setMenuOpen(!menuOpen)} className="text-white">
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Full Menu */}
      <div
        className={`${
          menuOpen ? "block" : "hidden"
        } xl:flex absolute xl:relative top-20 xl:top-0
 left-0 w-full xl:w-auto bg-gray-800 xl:bg-transparent z-50 xl:items-center flex-col xl:flex-row text-white xl:space-x-4`}
      >
        <Link
          to="/items"
          className="block px-4 py-2 hover:text-gray-400 transition duration-300"
        >
          All Items
        </Link>

        {/* My Item Trackers */}
        <div className="relative" ref={myItemsDropdownRef}>
          <button
            onClick={() => toggleDropdown("MyItemTrackers")}
            className="flex items-center gap-1 px-4 py-2 hover:text-gray-400 transition duration-300"
          >
            My Item Trackers
            {openDropdown === "MyItemTrackers" ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
          {openDropdown === "MyItemTrackers" && (
            <div className="absolute ml-4 bg-black text-white rounded-md shadow-md mt-3 z-10 w-48">
              {gamesNameOptions.length > 0 ? (
                gamesNameOptions.map((game) => (
                  <Link
                    key={game.id}
                    to={`/myitems/${game.name}`}
                    className="block px-4 py-2 hover:bg-gray-200"
                  >
                    {game.name}
                  </Link>
                ))
              ) : (
                <p className="px-4 py-2">No games available</p>
              )}
            </div>
          )}
        </div>

        <Link
          to="/friendlist"
          className="block px-4 py-2 hover:text-gray-400 transition duration-300"
        >
          Friend's Trackers
        </Link>

        {token && ["owner", "management", "helper", "cryo_mapper"].includes(token.account.role) && (
          <Link
            to="/lootmap/Cryo"
            className="block px-4 py-2 hover:text-gray-400 transition duration-300"
          >
            Cryo Loot Map
          </Link>
        )}

        {/* Puzzles Dropdown */}
        <div className="relative" ref={puzzlesDropdownRef}>
          <button
            onClick={() => toggleDropdown("Puzzles")}
            className="flex items-center gap-1 px-4 py-2 hover:text-gray-400 transition duration-300"
          >
            Puzzles
            {openDropdown === "Puzzles" ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
          {openDropdown === "Puzzles" && (
            <div className="absolute ml-4 bg-black text-white rounded-md shadow-md mt-3 z-10 w-52">
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Lights Out</div>
              <Link to="/LightsOut" className="block px-4 py-2 hover:bg-gray-200">Practice</Link>
              <Link to="/LightsOutSolver" className="block px-4 py-2 hover:bg-gray-200">Solver</Link>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-700 mt-1">Slider</div>
              <Link to="/SliderGame" className="block px-4 py-2 hover:bg-gray-200">Practice</Link>
              <Link to="/slider/solver" className="block px-4 py-2 hover:bg-gray-200">Solver</Link>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-700 mt-1">Key Puzzle</div>
              <Link to="/rush-hour/game" className="block px-4 py-2 hover:bg-gray-200">Practice</Link>
              <Link to="/rush-hour/solver" className="block px-4 py-2 hover:bg-gray-200">Solver</Link>
            </div>
          )}
        </div>

        {token && (
          <Link to="/friends" className="block px-4 py-2">
            <FriendIcon />
          </Link>
        )}

        {/* {token &&
          ["tester", "helper", "management", "owner"].includes(
            token.account.role
          ) && (
            <Link
              to="/myitems/LabyrinthineV2"
              className="block px-4 py-2 hover:text-gray-400 transition duration-300"
            >
              Testers
            </Link>
          )} */}

        {/* Manage Dropdown */}
        <div className="relative" ref={manageDropdownRef}>
          {token &&
            ["owner", "management", "helper", "linguist"].includes(token.account.role) && (
              <>
                <button
                  onClick={() => toggleDropdown("Manage")}
                  className="flex items-center gap-1 px-4 py-2 hover:text-gray-400 transition duration-300"
                >
                  Manage
                  {(reviewCounts.suggestions + reviewCounts.translations) > 0 && (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                      {reviewCounts.suggestions + reviewCounts.translations}
                    </span>
                  )}
                  {openDropdown === "Manage" ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
                {openDropdown === "Manage" && (
                  <div className="absolute ml-4 bg-gray-800 text-white rounded-md shadow-md mt-3 z-10 w-56">
                    {["owner", "management", "helper"].includes(token.account.role) && (
                      <Link to="/item/create" className="block px-4 py-2 hover:bg-gray-600">Add Item</Link>
                    )}
                    <Link to="/translations/submit" className="block px-4 py-2 hover:bg-gray-600">Submit Translations</Link>
                    {["management", "owner", "helper"].includes(token.account.role) && (
                      <>
                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-600 mt-1">Management</div>
                        <Link to="/announcements/manage" className="block px-4 py-2 hover:bg-gray-600">Manage Announcements</Link>
                        <Link to="/items/manage" className="block px-4 py-2 hover:bg-gray-600">Manage Items</Link>
                        <Link to="/images/manage" className="block px-4 py-2 hover:bg-gray-600">Manage Images</Link>
                        <Link to="/languages/manage" className="block px-4 py-2 hover:bg-gray-600">Manage Languages</Link>
                      </>
                    )}
                    {["management", "owner"].includes(token.account.role) && (
                      <>
                        <Link to="/accounts" className="block px-4 py-2 hover:bg-gray-600">Accounts</Link>

                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-600 mt-1">Review</div>
                        <Link to="/translations/review" className="flex items-center justify-between px-4 py-2 hover:bg-gray-600">
                          Review Translations
                          {reviewCounts.translations > 0 && (
                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                              {reviewCounts.translations}
                            </span>
                          )}
                        </Link>
                        <Link to="/suggestions/review" className="flex items-center justify-between px-4 py-2 hover:bg-gray-600">
                          Review Suggestions
                          {reviewCounts.suggestions > 0 && (
                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                              {reviewCounts.suggestions}
                            </span>
                          )}
                        </Link>

                        <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-600 mt-1">Addition</div>
                        <Link to="/event/create" className="block px-4 py-2 hover:bg-gray-600">Add Event</Link>
                        <Link to="/types/create" className="block px-4 py-2 hover:bg-gray-600">Add Category</Link>
                        <Link to="/maps/create" className="block px-4 py-2 hover:bg-gray-600">Add Type</Link>
                        <Link to="/mapsname/create" className="block px-4 py-2 hover:bg-gray-600">Add Map Name</Link>
                        <Link to="/games/create" className="block px-4 py-2 hover:bg-gray-600">Add Game</Link>
                        <Link to="/unlock-requirements/manage" className="block px-4 py-2 hover:bg-gray-600">Unlock Requirements</Link>
                        <Link to="/acquisition-methods/manage" className="block px-4 py-2 hover:bg-gray-600">Acquisition Methods</Link>
                        <Link to="/alljunk" className="block px-4 py-2 hover:bg-gray-600">Check Junk</Link>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
        </div>

        {/* Account Dropdown */}
        {token ? (
          <div className="relative" ref={accountDropdownRef}>
            <button
              onClick={() => toggleDropdown("Account")}
              className="flex items-center gap-1 px-4 py-2 hover:text-gray-400 transition duration-300"
            >
              Account
              {openDropdown === "Account" ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>
            {openDropdown === "Account" && (
              <div className="absolute left-4 xl:left-auto xl:right-4 bg-gray-800 text-white rounded-md shadow-md mt-3 z-10 w-48">
                <Link to="/myprofile" className="block px-4 py-2 hover:bg-gray-200">My Profile</Link>
                <Link to="/suggestions/submit" className="block px-4 py-2 hover:bg-gray-200">Send Feedback</Link>
                <Link to="/trusted-claimers" className="block px-4 py-2 hover:bg-gray-200">Trusted Claimers</Link>
                <Link to="/claims/review" className="flex items-center justify-between px-4 py-2 hover:bg-gray-200">
                  Pending Claims
                  {claimsCount > 0 && (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                      {claimsCount}
                    </span>
                  )}
                </Link>
                <div className="px-4 py-2 hover:bg-gray-200">
                  <Logout />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex justify-between items-center px-2 py-2 space-x-4 w-full lg:w-auto">
            <Link
              to="/login"
              className="py-1 text-white hover:text-gray-400 transition duration-300"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="py-1 px-2 text-white hover:text-gray-400 transition duration-300"
            >
              Sign Up
            </Link>
          </div>
        )}
      </div>
    </nav>
    <AnnouncementBar />
    </>
  );
}

export default Nav;
